import * as yup from 'yup';
import store from '../redux/store';
import { setAuthUser } from '../redux/reducer/authUserSlice';
var CryptoJS = require('crypto-js');

export function encryptText(plainText: any) {
  if (!plainText) return ''
  let key = CryptoJS.enc.Utf8.parse('2b7e151628aed2a6abf7158809cf4f3c');
  let iv = CryptoJS.enc.Utf8.parse('3ad77bb40d7a3660');
  let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
  let encPassword = CryptoJS.AES.encrypt(plainText, key, config);
  return encPassword.ciphertext.toString(CryptoJS.enc.Base64);
}

export const decryptText = (text: any) => {
  if (!text) return ''
  let key = CryptoJS.enc.Utf8.parse('2b7e151628aed2a6abf7158809cf4f3c');
  let iv = CryptoJS.enc.Utf8.parse('3ad77bb40d7a3660');
  let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
  try {
    let encPassword = CryptoJS.AES.decrypt(text, key, config);
    return encPassword.toString(CryptoJS.enc.Utf8);
  } catch (err) {
    try {
      return JSON.parse(text)
    } catch (err1) {
      return text || ''
    }
  }
}

const getAuthEncryptionKey = (): string => {
  const key = store.getState().authUser?.user?.encryption_key;
  if (!key) {
    throw new Error('Encryption key not found on auth user');
  }
  return key;
};

const base64ToBytes = (b64: string): Uint8Array =>
  Uint8Array.from(atob(b64), c => c.charCodeAt(0));

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64UrlToBase64 = (b64url: string): string =>
  b64url.replace(/-/g, '+').replace(/_/g, '/');

const base64ToBase64Url = (b64: string): string =>
  b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export const decryptPayload = async (encryptedPayload: any): Promise<any> => {
  const base64Key = getAuthEncryptionKey();
  const keyBytes = base64ToBytes(base64Key);

  const decoded = base64ToBytes(base64UrlToBase64(encryptedPayload));
  const nonce = decoded.slice(0, 12);
  const ciphertext = decoded.slice(12);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes as BufferSource,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce as BufferSource },
    cryptoKey,
    ciphertext as BufferSource
  );

  return JSON.parse(new TextDecoder().decode(plaintext));
};

export const encryptPayload = async (payload: any): Promise<string> => {
  const base64Key = getAuthEncryptionKey();
  const keyBytes = base64ToBytes(base64Key);

  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = typeof payload === 'string' ? payload : JSON.stringify(payload);
  const plaintextBytes = new TextEncoder().encode(plaintext);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes as BufferSource,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );

  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: nonce as BufferSource },
      cryptoKey,
      plaintextBytes as BufferSource
    )
  );

  const combined = new Uint8Array(nonce.length + ciphertext.length);
  combined.set(nonce, 0);
  combined.set(ciphertext, nonce.length);

  return base64ToBase64Url(bytesToBase64(combined));
};

export const isObject = (obj: any) => typeof obj === 'object' && !Array.isArray(obj) && obj !== null

export const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export const getComparator = <Key extends keyof any>(
  order: any,
  orderBy: Key,
): (
  a: { [key in Key]: any },
  b: { [key in Key]: any },
) => number => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export const stableSort = <T>(array: readonly T[], comparator: (a: T, b: T) => number) => {
  const stabilizedThis: Array<any> = array?.map((el, index) => [el, index] as [T, number]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}

export function generateRandomColor() {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export const attendanceStatusConfig: Record<string, { label: string; color: string }> = {
  present: { label: "Present", color: "#4CAF50" },
  absent: { label: "Absent", color: "#F44336" },
  leave_count: { label: "Leave", color: "#770f00" },
  half_day: { label: "Half Day", color: "#FFD700" },
  week_off: { label: "Week Off", color: "#2196F3" },
  holiday: { label: "Holiday", color: "#9C27B0" },
};

// export const AllAttendanceStatusConfig: Record<string, { label: string; color: string }> = {
//   present: { label: "Present", color: "#4CAF50" },
//   absent: { label: "No Show", color: "#F44336" },
//   leave: { label: 'Leave', color: "#FFB74D" },
//   od: { label: 'Outdoor Duty', color: "#4CAF50" },
//   comp_off: { label: 'Comp Off', color: "#4CAF50" },
//   earned_leave: { label: 'Earned Leave', color: "#FFB74D" },
//   half_day: { label: 'Half Day', color: "#FF9800" },
//   week_off: { label: 'Week Off', color: "#2196F3" },
//   holiday: { label: 'Holiday', color: "#9C27B0" },
// }

// 'present','absent','leave','od','comp_off','earned_leave','half_day', week_off, holiday

export const APPROVER_CHAIN_MODULES = {
  LEAVE_APPROVAL: "leave_approval",
  ATTENDANCE_REGULARIZATION: "attendance_regularization",
  BACK_DATED_ATTENDANCE_REGULARIZATION: "back_dated_attendance_regularization",
  GRACE_PERIOD: "grace_period",
  OUTDOOR_DUTY: "outdoor_duty",
  COMP_OFF: "comp_off",
  PROBATION: "probation",
  EXIT: "exit",
  CLAIM: "claim",
  EXIT_CLEARANCE: "exit_clearance",
} as const;



// export const getFinancialYearMonths = (): string[] => {
//     const currentDate = new Date();
//     const currentYear = currentDate.getFullYear();
//     const currentMonth = currentDate.getMonth();

//     const startYear = currentMonth >= 3 ? currentYear : currentYear - 1;
//     const months = [
//         "April", "May", "June", "July", "August", "September",
//         "October", "November", "December", "January", "February", "March"
//     ];

//     return months.map((month, index) => {
//         const year = index >= 9 ? startYear + 1 : startYear;
//         return `${month} ${year}`;
//     });
// };

export const getFinancialYearMonths = (): { label: string, value: number }[] => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const startYear = currentMonth >= 3 ? currentYear : currentYear - 1;
  const months = [
    "April", "May", "June", "July", "August", "September",
    "October", "November", "December", "January", "February", "March"
  ];

  return months.map((month, index) => {
    const year = index >= 9 ? startYear + 1 : startYear;
    const monthNumber = (index + 3) % 12 + 1;
    return { label: `${month} ${year}`, value: monthNumber };
  });
};



export const rupeeAmountDisplay = (amount: number) => {
  const formattedAmount = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(amount);

  return formattedAmount;
};


export const getMonthName = (monthNumber: number): string => {
  const months = [
    "January", "February", "March", "April", "May", "June", "July", "August", "September",
    "October", "November", "December",
  ];
  return months[monthNumber - 1];
};

export const getFinancialYears = (range = 5) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const startYear = currentMonth >= 3 ? currentYear : currentYear - 1; // If it's April or later, start with the current year

  const years = [];
  for (let i = -range; i <= range; i++) {
    const year1 = startYear + i;
    const year2 = year1 + 1;
    years.push(`${year1}-${year2}`);
  }
  return years;
}

export const getCurrentFinancialYear = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-based

  // If the month is between April (4) and December (12), the financial year starts in the current year
  if (currentMonth >= 4) {
    return `${currentYear}-${currentYear + 1}`;
  } else {
    // If the month is between January (1) and March (3), the financial year started last year
    return `${currentYear - 1}-${currentYear}`;
  }
};

// Function to get a unique color from a specific color set
export const getRandomColor = (colorPalette: string[], usedColorsSet: Set<string>) => {
  let availableColors = colorPalette.filter(color => !usedColorsSet.has(color));

  if (availableColors.length === 0) {
    // Reset if all colors are used
    usedColorsSet.clear();
    availableColors = [...colorPalette]; // Clone array
  }

  const color = availableColors[Math.floor(Math.random() * availableColors.length)];
  usedColorsSet.add(color);
  return color;
};

export const getFinancialPrevYears = (range = 5) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const startYear = currentMonth >= 3 ? currentYear : currentYear - 1; // Determine the current financial year start

  const years = [];
  for (let i = range; i >= 0; i--) { // Include the current financial year
    const year1 = startYear - i;
    const year2 = year1 + 1;
    years.push(`${year1}-${year2}`);
  }

  return years;
};
  
export const getAssessmentYears = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const startYear = currentMonth >= 3 ? currentYear : currentYear - 1; // If it's April or later, start with the current year

  const years = [];

  // Generate past 5 years
  for (let i = -1; i <= 1; i++) { // -5 for past years, 1 for only one future year
    const year1 = startYear + i;
    const year2 = year1 + 1;
    years.push(`${year1}-${year2}`);
  }

  return years;
};






//------------ for CLock IN / CLock OUt ------------------------



type Location = {    
  lat: number;
  long: number;
};

function isUserAllowedToClockIn(currentLatitude: number, currentLongitude: number, accessLocationsArray: Location[]) {
  const R = 6371000; // Earth's radius in meters

  const toRadians = (degrees: number): number => {
      return (degrees * Math.PI) / 180;
  };

  function getDistance(currentLat: number, currentLong: number, accessLat: number, accessLong: number) {
      const dLat = toRadians(accessLat - currentLat);
      const dLon = toRadians(accessLong - currentLong);

      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRadians(currentLat)) * Math.cos(toRadians(accessLat)) *
          Math.sin(dLon / 2) * Math.sin(dLon / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
  }

  return accessLocationsArray?.some((location: Location) => {
      const distance = getDistance(currentLatitude, currentLongitude, location.lat, location.long);
      console.log("location.lat:", location.lat);
      console.log("location.long:", location.long);
      console.log("distance:", distance);
      return distance <= 100; // Check if within 100 meters
  });
}

function formatToNumber(input: any) {
  const total = parseFloat(input);
  if (!isNaN(total)) {
      return total;
  } else {
      return 0.0;
  }
}

export const checkISAllowForClockIn = (position: any,clientMappingArray:any,branchMappingArray:any) => {
  let isValid: boolean = false;
  let accessLocationsArray: any = [];
  let subClientArray: any = [];
  clientMappingArray.map((clientObj: any, index: any) => {
      let parseArray = JSON.parse(clientObj.client_details) || [];
      subClientArray = [...subClientArray, ...parseArray];
  });

  subClientArray?.map((obj: any, index: any) => {
      if (obj?.is_active === "Y") {
          let clientslocations = {
              lat: formatToNumber(obj?.lattitude),
              long: formatToNumber(obj?.longitude)
          };
          accessLocationsArray?.push(clientslocations)
      }
  });


  branchMappingArray?.map((branchObj: any, index: any) => {
      let branchLocations = {
          lat: formatToNumber(branchObj?.latitude),
          long: formatToNumber(branchObj?.longitude)
      };
      accessLocationsArray?.push(branchLocations)
  });

  console.log("currentlat:", position.coords.latitude);
  console.log("currentLong:", position.coords.longitude);

  isValid = isUserAllowedToClockIn(position.coords.latitude, position.coords.longitude, accessLocationsArray);
  return isValid;
}


//------------ for CLock IN / CLock OUt ------------------------


export const accessTypeConfig: any = {
  company: { label: 'Company', color: 'primary' },
  vertical: { label: 'Vertical', color: 'primary' },
  branch: { label: 'Branch', color: 'primary' },
  department: { label: 'Department', color: 'primary' },
}

export const pincodeValidation =  yup.string().matches(/^[1-9][0-9]{5}$/, "Enter a valid 6-digit pincode starting from 1-9")

export const latitudeValidation = yup.number().typeError("Latitude must be a valid number").required("Please enter latitude").min(-90, "Latitude must be between -90 and 90").max(90, "Latitude must be between -90 and 90")

export const longitudeValidation =  yup.number().typeError("Longitude must be a valid number").required("Please enter longitude").min(-180, "Longitude must be between -180 and 180").max(180, "Longitude must be between -180 and 180")

export const emailValidation = yup.string().email('Invalid email address').email("Invalid email address").matches(/^(?!.*\.\.)[a-zA-Z0-9](\.?[a-zA-Z0-9_\-+])*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[A-Za-z]{2,}$/,"Please enter a valid email (e.g., yourname@example.com). Only letters, numbers, and ., _, -, + are allowed before the '@'.");

export const mobileNumberValidation = yup.string().trim().required("Please enter your mobile number.").matches(/^[6-9]\d{9}$/,  "Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.")


export const updateAcknowledgementInLocalState = (actionType: any,dispatch:any) => {
  const user = store.getState().authUser?.user ?? {}; 
  let res = {   ...store.getState().authUser, user: {  ...user}  } ; 

  if (actionType === "familyDetails" && user?.family_details_pending === "Y" ) {
     res = {   ...store.getState().authUser, user: {  ...user,  family_details_pending: "N"  }  } ;  
  }

  if (actionType === "addressDetails" && user?.address_details_pending === "Y" ) {
    res = {   ...store.getState().authUser, user: {  ...user,  address_details_pending: "N"  }  } ;  
  }

  if (actionType === "educationDetails" && user?.education_details_pending === "Y" ) {
    res = {   ...store.getState().authUser, user: {  ...user,  education_details_pending: "N"  }  } ;  
  }

  if (actionType === "emergencyContactDetails" && user?.emergency_contact_details_pending === "Y" ) {
    res = {   ...store.getState().authUser, user: {  ...user,  emergency_contact_details_pending: "N"  }  } ;  
  }

    const encrypted = JSON.stringify(res);
    localStorage.setItem('user', encryptText(encrypted)); //user-details update in localStorage 
    dispatch(setAuthUser(res));  //user-details update in redux 
};

export const getMimeTypeFromPath = (path: string): string => {
    const ext = path.split('.').pop()?.toLowerCase();
    switch (ext) {
        case 'pdf': return 'application/pdf';
        case 'jpg':
        case 'jpeg': return 'image/jpeg';
        case 'png': return 'image/png';
        case 'doc': return 'application/msword';
        case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        default: return 'application/octet-stream';
    }
};

export const getExtensionFromType = (type: string): string => {
    switch (type) {
        case 'application/pdf': return '.pdf';
        case 'image/jpeg': return '.jpg';
        case 'image/png': return '.png';
        case 'application/msword': return '.doc';
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': return '.docx';
        default: return '';
    }
};
export const ComponentNotIncludedInCTC = [
   "CTC",
   "GROSS",
   "GROSS_annual", 
   "net_take_home_annual", 
   "net_take_home_monthly", 
   "EMPLOYEE_EPF_annual", 
   "EMPLOYEE_EPF", 
   "EMPLOYEE_VPF_annual", 
   "EMPLOYEE_VPF", 
   "EMPLOYEE_ESI_annual", 
   "EMPLOYEE_ESI",
   "UNIFORM_ALLOWANCE_DEDUCTION",
   "UNIFORM_ALLOWANCE_DEDUCTION_annual"
  ]






  export function formatLabel(text: any) {
    try {
      if (typeof text !== "string" || !text) return "";

      if(text === "spot_resign"){
       return "Backdated Exit";
      }
  
      return text
        .split("_")
        .map(word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    } catch (e) {
      return text;
    }
  }
  
  export const CTCAdditionComponent = [
    "GROSS", 
      "ANNUAL_BONUS_annual",
      "ANNUAL_LEAVE_SALARY_annual",
      "ANNUAL_VARIABLE_PAY_annual",
      "ANNUAL_PERFORMANCE_LINKED_BONUS_annual",
      "ANNUAL_VARIABLE_PAY_annual",
      "CAR_EXPENSE_annual",
      "CORPORATE_NPS_annual",
      "EMPLOYER_ESI_annual",
      "CHAFFEUR_ALLOWANCE_annual",
      "HALF_YEARLY_BONUS_annual",
      "JOINING_BONUS_annual",
      "EMPLOYER_LWF_annual",
      "UNIFORM_ALLOWANCE_DEDUCTION_annual",
      "UNIFORM_ALLOWANCE_annual",
      "EMPLOYER_EPF_annual",
      "PERFORMANCE_BASED_INCENTIVES_annual",
      "QUARTERLY_PERFORMANCE_LINKED_BONUS_annual", 
      "GROSS_annual",
      "ANNUAL_BONUS",
      "ANNUAL_LEAVE_SALARY",
      "ANNUAL_VARIABLE_PAY",
      "ANNUAL_PERFORMANCE_LINKED_BONUS",
      "ANNUAL_VARIABLE_PAY",
      "CAR_EXPENSE",
      "CORPORATE_NPS",
      "EMPLOYER_ESI",
      "CHAFFEUR_ALLOWANCE",
      "HALF_YEARLY_BONUS",
      "JOINING_BONUS",
      "EMPLOYER_LWF",
      "UNIFORM_ALLOWANCE_DEDUCTION",
      "UNIFORM_ALLOWANCE",
      "EMPLOYER_EPF",
      "PERFORMANCE_BASED_INCENTIVES",
      "QUARTERLY_PERFORMANCE_LINKED_BONUS"
    ];
  
    export const isInvalidDate = (date?: string) => {
      return !date || date.startsWith("0001-01-01");
    };
 
    export  function rearrangeCTC(offerCtcBreakup:any) {
      // Define the desired sequence of component slugs
      const sequence = [
          "BASIC",
          "HRA",
          "LEAVE_TRAVEL_ALLOWANCE",
          "MEDICAL_REIMBURSEMENT",
          "RESEARCH_ALLOWANCE",
          "CAR_EXPENSE",
          "ADVANCE_AGAINST_STATUTORY_BONUS",
          "LEAVE_SALARY",
          "SPECIAL_ALLOWANCE",
          "GROSS",
          "PERFORMANCE_LINKED_BONUS",
          "ANNUAL_BONUS",
          "HALF_YEARLY_BONUS",
          "PERFORMANCE_BASED_INCENTIVES",
          "QUARTERLY_PERFORMANCE_LINKED_BONUS",
          "PT"
      ];
  
      // Check if input is an array
      if (!Array.isArray(offerCtcBreakup)) {
          return offerCtcBreakup;
      }
  
      // Create a copy of the input array to avoid mutating it
      const sortedBreakup = [...offerCtcBreakup].sort((a, b) => {
          const slugA = a.component_slug || a.slug || '';
          const slugB = b.component_slug || b.slug || '';
          const indexA = sequence.indexOf(slugA);
          const indexB = sequence.indexOf(slugB);
  
          // If both slugs are in the sequence, sort by sequence order
          if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
          }
          // If only A is in sequence, A comes first
          if (indexA !== -1) {
              return -1;
          }
          // If only B is in sequence, B comes first
          if (indexB !== -1) {
              return 1;
          }
          // If neither is in sequence, sort alphabetically
          return slugA.localeCompare(slugB);
      });
  
      return sortedBreakup;
  }
